<script lang="ts">
  import { onMount } from 'svelte';
  import Router from 'svelte-spa-router';
  import { location } from 'svelte-spa-router';
  import { isLoading, _ } from 'svelte-i18n';
  import routes from './router';
  import Nav from './components/Nav.svelte';
  import MigrationBanner from './components/MigrationBanner.svelte';
  import "./theme.scss";
  import { settingsStore, refreshSettings } from './stores/settings';
  import { auth } from './lib/auth';

  // Theme state
  let lightMode = localStorage.getItem('theme') === 'light' || false;
  
  function toggleTheme() {
    lightMode = !lightMode;
    updateTheme();
  }
  
  function updateTheme() {
    if (lightMode) {
      document.documentElement.setAttribute('data-theme', 'light');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    }
  }
   
  let currentRoute = '';
  $: currentRoute = $location;

  // Check if user has email for migration banner (hide on match pages which have their own header)
  $: showMigrationBanner = $settingsStore.user && !$settingsStore.user.email && !shouldHideNavigation(currentRoute);

  // Function to check if navigation should be hidden for the current route
  function shouldHideNavigation(route: string): boolean {
    // Check if the current route matches '/match/:id/:view?' or '/share/:token'
    return route.startsWith('/match/') || route.startsWith('/share/');
  }

  // Function to check if route is public (doesn't require authentication)
  function isPublicRoute(route: string): boolean {
    return route.startsWith('/share/');
  }
  
  // Function to redirect to the account app login page
  function redirectToLogin() {
    // Get the current path (without the domain)
    let currentPath = window.location.pathname + window.location.hash;
    
    // Ensure the path is relative and starts with /
    if (!currentPath.startsWith('/')) {
      currentPath = '/' + currentPath;
    }
    
    // Strip any protocol and domain if somehow present
    currentPath = currentPath.replace(/^(?:https?:)?(?:\/\/[^\/]+)?(\/.*)?$/, '$1');
    
    // If empty, default to root
    if (!currentPath) {
      currentPath = '/';
    }
    
    // Create the return_to parameter with the encoded current path
    const returnToParam = encodeURIComponent(currentPath);
    
    // Redirect to the account login page with the return_to parameter
    window.location.href = `/account/#/login?return_to=${returnToParam}`;
  }
  
  onMount(() => {
    // Initialize theme
    updateTheme();
    
    // Load settings
    refreshSettings();
  });
  
</script>

<svelte:head>
  <script type="module" src="https://cdn.jsdelivr.net/npm/ionicons@7.1.0/dist/ionicons/ionicons.esm.js"></script>
  <script nomodule src="https://cdn.jsdelivr.net/npm/ionicons@7.1.0/dist/ionicons/ionicons.js"></script>
</svelte:head>

<!-- Main container -->
<div class="container">
  <!-- Header/Navigation -->
  {#if $isLoading}
    <div class="loading">Loading...</div>
  {:else if !shouldHideNavigation(currentRoute)}
    <Nav darkMode={!lightMode} {toggleTheme} user={$settingsStore.user}/>
  {/if}

  <!-- Main content -->
  <main>
    {#if $isLoading}
      <p>Loading...</p>
    {:else if $auth.token || isPublicRoute(currentRoute)}
      <MigrationBanner show={showMigrationBanner} />
      <Router {routes} />
    {:else}
      {redirectToLogin()}
      <p>Redirecting to login page...</p>
    {/if}
  </main>
  
  <!-- Toast container for notifications -->
  <div id="toast-container"></div>
</div>

<style>
  main {
    padding-top: 0rem;
    padding-bottom: 3rem;
    max-width: 768px;
    margin: 0 auto;

  }
</style> 